import { FolderArchiveIcon, LogOutIcon, WalletIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/alert-dialog';
import { Badge } from '@/components/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/dropdown-menu';
import PATH from '@/constants/path';
import useGetPlan from '@/hooks/use-get-plan.hook';
import useGetWallet from '@/hooks/use-get-wallet.hook';
import useUser from '@/hooks/use-user.hook';
import cn from '@/utils/cn.util';

const LoggedMenuItems = (): React.JSX.Element[] => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const { user, logoff } = useUser();
  const { wallet } = useGetWallet();
  const { plans } = useGetPlan();

  const currentPath = window.location.pathname;
  const planName =
    user &&
    plans.find(p => p.type.toString() === user.basePlanType.toString())?.name;

  return [
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="border-0 rounded-full w-auto h-auto cursor-pointer flex items-center shadow-md">
          <img
            className="h-8 w-8 rounded-full object-cover"
            src={user?.picture ?? 'https://www.gravatar.com/avatar/?d=mp'}
            alt="profile"
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-auto max-w-[240px] bg-white text-grayish"
      >
        {planName ? (
          <>
            <DropdownMenuItem
              className={cn(
                'text-grayish text-sm text-ellipsis overflow-hidden justify-center text-center w-full',
                wallet.balance === 0 && 'cursor-pointer focus:text-primary'
              )}
            >
              <div className="flex flex-col items-center gap-1">
                <Badge className="bg-white border border-primary text-primary font-regular focus:text-white hover:text-white">
                  {planName}
                </Badge>
                {t('header.balance', { count: wallet.balance })}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-grayish/50" />
          </>
        ) : null}
        {currentPath !== PATH.DASHBOARD.INDEX ? (
          <DropdownMenuItem
            onClick={() => navigate(PATH.DASHBOARD.INDEX)}
            className={'cursor-pointer focus:text-primary'}
          >
            <FolderArchiveIcon className="w-4 h-4 mr-2" />
            {t('header.reports')}
          </DropdownMenuItem>
        ) : null}
        {currentPath !== PATH.DASHBOARD.FINANCIAL ? (
          <DropdownMenuItem
            onClick={() => navigate(PATH.DASHBOARD.FINANCIAL)}
            className={'cursor-pointer focus:text-primary'}
          >
            <WalletIcon className="w-4 h-4 mr-2" />
            {t('header.wallet')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onSelect={e => e.preventDefault()}
          className="cursor-pointer focus:text-primary"
        >
          <AlertDialog
            title={t('header.logout_confirmation.title')}
            description={t('header.logout_confirmation.description')}
            trigger={
              <div className="flex items-center w-full">
                <LogOutIcon className="w-4 h-4 mr-2" />
                {t('header.logout')}
              </div>
            }
          >
            <AlertDialogCancel>
              {t('header.logout_confirmation.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={logoff}>
              {t('header.logout_confirmation.confirm')}
            </AlertDialogAction>
          </AlertDialog>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,
  ];
};

export default LoggedMenuItems;
